import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  JsonApiResponseDto,
  Request,
  Response,
  TQueryParams,
  formatQueryParams,
  formatRequest,
  parseJsonApiResponse,
  prepareHeaders,
} from 'evolve-ui';
import { TOrganizationalEntity } from 'evolve-ui/src/common/types/organizational-entity';

export enum CampaignTemplateStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum CommunicationOperationTypes {
  OPERATION = 'operation',
  MARKETING = 'marketing',
  MARKETING_OFFERS = 'marketing_offers',
}

export enum CampaignTemplateCategories {
  CUSTOMERS = 'customers',
  EMPLOYEES = 'employees',
}

export enum CampaignTemplateCommunicationChannels {
  SMS = 'sms',
  EMAIL = 'email',
}

export enum SenderProfiles {
  GENERAL = 'general',
  PERSONAL = 'personal',
}

export type TCampaignTemplateData = {
  // * EMAIL
  templateId?: string;
  templateName?: string;
  profile?: SenderProfiles;
  editable?: boolean;

  // * SMS
  smsContent?: string;
};

// * CampaignTemplate object type
export type TCampaignTemplate = {
  id?: string;
  type?: 'campaign-template';

  name?: string;
  status?: CampaignTemplateStatus;
  category?: CampaignTemplateCategories;
  channel?: CampaignTemplateCommunicationChannels;
  allowedUsers?: TOrganizationalEntity[];
  initiative?: TOrganizationalEntity;
  data?: TCampaignTemplateData;
  communicationType?: CommunicationOperationTypes;
};

export type TSendgridTemplate = {
  id?: string;
  html?: string;
  name?: string;
  plain?: string;
  subject?: string;
};

export const campaignTemplatesApi = createApi({
  reducerPath: 'campaign-templates',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.GATSBY_CAMPAIGNS_TEMPLATES_API_URL}`,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['CampaignTemplate'],
  endpoints: (builder) => {
    return {
      // * GET
      getCampaignTemplates: builder.query<
        Response<TCampaignTemplate>,
        TQueryParams
      >({
        providesTags: ['CampaignTemplate'],
        query: formatQueryParams,
        transformResponse: (res, meta, args) => {
          return parseJsonApiResponse<TCampaignTemplate>(
            res as JsonApiResponseDto<TCampaignTemplate>,
            meta,
            args,
            true
          );
        },
      }),

      getSingleCampaignTemplate: builder.query<
        Response<TCampaignTemplate>,
        { campaignTemplateId: string }
      >({
        providesTags: ['CampaignTemplate'],
        query: ({ campaignTemplateId }) => {
          return formatQueryParams({}, `${campaignTemplateId}`);
        },
        transformResponse: parseJsonApiResponse<TCampaignTemplate>,
      }),

      getTemplates: builder.query<Response<TCampaignTemplate>, TQueryParams>({
        providesTags: ['CampaignTemplate'],
        query: (params) => formatQueryParams(params, 'sendgrid'),
        transformResponse: parseJsonApiResponse<TCampaignTemplate>,
      }),

      getSingleTemplate: builder.query<
        Response<TSendgridTemplate>,
        { templateId: string }
      >({
        providesTags: ['CampaignTemplate'],
        query: ({ templateId }) =>
          formatQueryParams({}, `sendgrid/${templateId}`),
        transformResponse: parseJsonApiResponse<TSendgridTemplate>,
      }),

      // * POST
      postCampaignTemplate: builder.mutation<
        Response<TCampaignTemplate>,
        Omit<TCampaignTemplate, 'id'>
      >({
        invalidatesTags: ['CampaignTemplate'],
        query: (params) =>
          formatRequest(
            { ...params, type: 'campaign-templates' },
            Request.POST,
            ''
          ),
        transformResponse: parseJsonApiResponse<TCampaignTemplate>,
      }),

      // * PATCH
      patchCampaignTemplate: builder.mutation<
        Response<TCampaignTemplate>,
        Partial<TCampaignTemplate> & {
          campaignTemplateId: string;
        }
      >({
        invalidatesTags: ['CampaignTemplate'],
        query: ({ campaignTemplateId, ...params }) =>
          formatRequest(
            { ...params, type: 'campaign-templates' },
            Request.PATCH,
            campaignTemplateId
          ),
        transformResponse: parseJsonApiResponse<TCampaignTemplate>,
      }),

      // * DELETE
      deleteCampaignTemplate: builder.mutation<
        Response<TCampaignTemplate>,
        { id: string }
      >({
        invalidatesTags: ['CampaignTemplate'],
        query: ({ id, ...params }) =>
          formatRequest(
            { ...params, type: 'campaign-templates' },
            Request.DELETE,
            id
          ),
        transformResponse: parseJsonApiResponse<TCampaignTemplate>,
      }),
    };
  },
});

export const {
  // * GET
  useGetCampaignTemplatesQuery,
  useGetSingleCampaignTemplateQuery,
  useGetTemplatesQuery,
  useGetSingleTemplateQuery,
  // * POST
  usePostCampaignTemplateMutation,
  // * PATCH
  usePatchCampaignTemplateMutation,
  // * DELETE
  useDeleteCampaignTemplateMutation,
} = campaignTemplatesApi;
